import { useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { toast } from 'sonner'
import { useLocation } from 'react-router-dom'
import {
  Accessibility,
  CircleDot,
  Globe2,
  Monitor,
  RotateCcw,
  Save,
  Undo2,
} from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { CompanyRegionSettings } from '../../components/CompanyRegionSettings'
import { useCanManageCompany } from '../../lib/use-can-manage-company'
import { scrollAndFlash } from '../../lib/focusFlash'
import { useAuthStore } from '../../stores/authStore'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import {
  datePattern,
  formatDateTime,
  getCompanyTimeZone,
  getDisplayTimeZone,
  timePattern,
} from '../../lib/format'
import { extractApiError } from '../../lib/api'
import {
  discardDisplayPreview,
  previewDisplayDefaults,
  previewDisplayPref,
  saveDisplayPreview,
  useDisplayPrefs,
} from '../../lib/display-prefs'
import { DISPLAY_PREFERENCES, TEXT_SIZE_SCALE } from '../../lib/display-prefs-defs'

/**
 * My Preferences — the person's own display & accessibility settings.
 *
 * Every change PREVIEWS at once (the whole app re-renders with it) but nothing
 * is saved until "Save Changes" (top right, like the other settings pages);
 * "Discard changes" puts the saved set back
 * without a request, and "Reset to defaults" previews the defaults (still
 * needs Save). Leaving the page with unsaved changes discards the preview, so
 * an experiment never leaks onto other screens; closing or reloading the tab
 * asks first.
 *
 * Reads and writes ONLY the signed-in user's own preferences (PUT
 * /me/preferences uses the session user; there is no user id to pass). The
 * header theme switch and text-size menu write the same values.
 *
 * People who can manage the company (`company.manage`) also get a "Company
 * settings" block at the top of Region & formats — the company's country and
 * timezone (moved here from My Profile). These company settings are
 * company-wide, save on their own buttons through PATCH /admin/company, and
 * are never part of Save Changes / Discard / Reset.
 *
 * "Your timezone" (same as the company, or a different one) is offered in the
 * member panel only — its route passes `personalTimezone`. The company-admin,
 * sub-admin and Master Admin panels work in the company timezone, so an admin
 * sees a single timezone setting (the company's).
 */

const TRI = [
  { value: 'system', label: 'Computer' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
]

const OPTIONS = {
  theme: [
    { value: 'system', label: 'Computer' },
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
  ],
  textSize: [
    { value: 'compact', label: 'Compact' },
    { value: 'normal', label: 'Normal' },
    { value: 'large', label: 'Large' },
    { value: 'larger', label: 'Larger' },
    { value: 'largest', label: 'Largest' },
  ],
  rowDensity: [
    { value: 'compact', label: 'Compact' },
    { value: 'comfortable', label: 'Comfortable' },
    { value: 'relaxed', label: 'Relaxed' },
  ],
  rowsPerPage: DISPLAY_PREFERENCES.rowsPerPage.values.map((n) => ({
    value: n,
    label: String(n),
  })),
  reduceMotion: TRI,
  contrast: TRI,
  underlineLinks: TRI,
  // Region & formats — each option is labelled with a live sample of itself
  // (renderOption below), so nobody needs to know what "dmy" means.
  dateFormat: [
    { value: 'dmy', label: 'Day first' },
    { value: 'mdy', label: 'Month first' },
    { value: 'iso', label: 'Year first' },
    { value: 'long', label: 'Written out' },
  ],
  timeFormat: [
    { value: '12', label: '12-hour' },
    { value: '24', label: '24-hour' },
  ],
  weekStart: [
    { value: 'mon', label: 'Monday' },
    { value: 'sun', label: 'Sunday' },
  ],
  numberFormat: [
    { value: 'comma', label: '1,234.56' },
    { value: 'dot', label: '1.234,56' },
    { value: 'space', label: '1 234,56' },
  ],
}

const LABELS = {
  theme: 'Theme',
  textSize: 'Text size',
  rowDensity: 'Row height',
  rowsPerPage: 'Rows per page',
  reduceMotion: 'Reduce movement',
  contrast: 'Increase contrast',
  underlineLinks: 'Always underline links',
  timezone: 'Timezone',
  dateFormat: 'Date format',
  timeFormat: 'Time format',
  weekStart: 'First day of the week',
  numberFormat: 'Number format',
}

const optionLabel = (key, value) =>
  OPTIONS[key]?.find((o) => o.value === value)?.label ?? String(value)

// Live samples for the format pickers (Luxon tokens; same patterns as
// lib/format.js).
const DATE_SAMPLE = { dmy: 'dd/MM/yyyy', mdy: 'MM/dd/yyyy', iso: 'yyyy-MM-dd', long: 'd MMM yyyy' }
const TIME_SAMPLE = { 12: 'hh:mm a', 24: 'HH:mm' }
const upper = (s) => s.replace(/\b(am|pm)\b/i, (m) => m.toUpperCase())

export function PreferencesPage({ homePath, homeLabel, padded, personalTimezone = false } = {}) {
  const { saved, effective, changedCount, saving, system } = useDisplayPrefs()
  const [announcement, setAnnouncement] = useState('')
  const canManageCompany = useCanManageCompany()
  // Subscribed so the "Company timezone (…)" option and the samples redraw as
  // soon as the company timezone is saved in the section below.
  const companyTimeZone = useAuthStore((s) => s.tenant?.timezone) || null

  // `#company` (the link under Reminders & Schedule's time pickers) scrolls to
  // the company section and flashes it. Uses the shared deep-link helper, which
  // polls — so it lands after the layout's scroll-to-top on navigation.
  const { hash } = useLocation()
  useEffect(() => {
    if (hash !== '#company' || !canManageCompany) return undefined
    const stop = scrollAndFlash('company')
    // Safety net: the layout's scroll-to-top on navigation can land after the
    // smooth scroll has started and cancel it, leaving the page at the top.
    // If the company fields still aren't on screen shortly after, bring them
    // into view again.
    const timers = [900, 1800].map((ms) =>
      setTimeout(() => {
        const el = document.getElementById('company')
        if (!el) return
        const { top } = el.getBoundingClientRect()
        if (top < 0 || top > window.innerHeight - 80) el.scrollIntoView({ block: 'center' })
      }, ms),
    )
    return () => {
      stop()
      timers.forEach(clearTimeout)
    }
  }, [hash, canManageCompany])

  // Leaving the page throws the unsaved preview away, so an experiment never
  // follows the person onto other screens.
  useEffect(() => () => discardDisplayPreview(), [])

  // Closing / reloading the tab with unsaved changes asks first.
  useEffect(() => {
    if (changedCount === 0) return undefined
    const onBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [changedCount])

  const change = (key, value) => {
    previewDisplayPref(key, value)
    setAnnouncement(`${LABELS[key]}: ${optionLabel(key, value)}. Not saved yet.`)
  }

  const onSave = async () => {
    try {
      await saveDisplayPreview()
      toast.success('Your preferences have been saved.')
      setAnnouncement('Preferences saved.')
    } catch (err) {
      toast.error(extractApiError(err).message || 'Your preferences could not be saved.')
    }
  }

  const onDiscard = () => {
    discardDisplayPreview()
    setAnnouncement('Changes discarded. Your saved preferences are back.')
  }

  const onReset = () => {
    previewDisplayDefaults()
    setAnnouncement('Default settings previewed. Select Save Changes to keep them.')
  }

  const row = (key, extra = {}) => (
    <OptionGroup
      key={key}
      name={key}
      legend={LABELS[key]}
      options={OPTIONS[key]}
      value={effective[key]}
      savedValue={saved[key]}
      onChange={(v) => change(key, v)}
      {...extra}
    />
  )

  const body = (
    <>
      <Breadcrumb current="My Preferences" homePath={homePath} homeLabel={homeLabel} />

      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 max-w-3xl">
          <h1 className="text-3xl font-semibold text-[color:var(--color-ink-strong)]">
            My Preferences
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            How TaskMagix looks and behaves for you. These settings belong to your
            account, follow you to any computer you sign in on, and don’t affect anyone else
            {canManageCompany
              ? ' — except Company country and Company timezone, which apply to the whole company.'
              : '.'}
          </p>
        </div>
        {/* Persistent Save action in the page header — the same placement and
         *  styling as the other settings pages (e.g. Gamification): always
         *  visible, disabled until something changes; Discard appears only
         *  when there is something to discard. */}
        <div
          role="group"
          aria-label="Save your preferences"
          className="flex flex-wrap items-center gap-2 shrink-0"
        >
          {changedCount > 0 ? (
            <>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[color:var(--color-warning)]">
                <CircleDot size={12} aria-hidden="true" />
                {changedCount === 1 ? '1 unsaved change' : `${changedCount} unsaved changes`}
              </span>
              <button
                type="button"
                onClick={onDiscard}
                disabled={saving}
                title="Go back to your saved preferences"
                className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg text-sm font-medium text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Undo2 size={14} aria-hidden="true" />
                Discard changes
              </button>
            </>
          ) : null}
          <button
            type="button"
            onClick={onSave}
            disabled={changedCount === 0 || saving}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={14} aria-hidden="true" />
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Polite live region — announces each previewed change, save and discard. */}
      <p className="sr-only" role="status" aria-live="polite">
        {announcement}
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <section
          aria-labelledby="prefs-appearance"
          className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm"
        >
          <SectionHeader id="prefs-appearance" icon={Monitor} title="Appearance" />
          <div className="px-6">
            {row('theme', {
              description: 'Light, dark, or whatever your computer is set to.',
              hint: `Your computer is set to ${system.prefersDark ? 'dark' : 'light'}.`,
            })}
            {row('textSize', {
              description: 'Makes the words larger or smaller. Spacing and layout stay as they are.',
              renderOption: (o) => (
                <span className="inline-flex items-baseline gap-1.5">
                  <span
                    aria-hidden="true"
                    className="font-semibold leading-none"
                    style={{ fontSize: `${Math.round(13 * TEXT_SIZE_SCALE[o.value])}px` }}
                  >
                    Aa
                  </span>
                  {o.label}
                </span>
              ),
            })}
            {row('rowDensity', {
              description:
                'The starting row height for tables. A height you pick in a table’s own menu still applies to that table.',
            })}
            {row('rowsPerPage', {
              description:
                'How many rows a table shows at first. Lists that set their own page size keep it.',
            })}
          </div>
        </section>

        <section
          aria-labelledby="prefs-accessibility"
          className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm"
        >
          <SectionHeader id="prefs-accessibility" icon={Accessibility} title="Accessibility" />
          <div className="px-6">
            {row('reduceMotion', {
              description: 'Turns off sliding, fading and other movement. Loading indicators keep working.',
              hint: system.prefersReducedMotion
                ? 'Your computer asks for reduced movement.'
                : 'Your computer asks for normal movement.',
            })}
            {row('contrast', {
              description: 'Stronger borders, darker secondary text and a clearer focus outline.',
              hint: system.prefersMoreContrast
                ? 'Your computer asks for more contrast.'
                : 'Your computer asks for normal contrast.',
            })}
            {row('underlineLinks', {
              description: 'Underlines text links everywhere, so they don’t rely on colour alone.',
              hint: 'Computer keeps TaskMagix’s standard link style.',
            })}
          </div>
          <p className="mx-6 mb-5 mt-1 text-xs text-[color:var(--color-ink-muted)]">
            <strong className="font-semibold">Computer</strong> follows your computer’s own
            setting and changes when it does. Choose On or Off to override it.
          </p>
        </section>
      </div>

      <RegionSection
        effective={effective}
        saved={saved}
        row={row}
        // "Your timezone" is offered in the member panel only (the route sets
        // `personalTimezone`); the admin panels work in the company timezone.
        // A personal zone already saved on an admin account still shows the
        // row, so it can be put back to the company's rather than lingering
        // unseen — it hides again once that is saved.
        showTimezone={personalTimezone || saved.timezone !== 'company'}
        companyTimeZone={companyTimeZone}
        // Company country + timezone (company-wide; their own Save buttons) sit
        // at the top of Region & formats for people who can manage the company.
        companyBlock={
          canManageCompany ? (
            <div
              id="company"
              data-focus-id="company"
              className="xl:col-span-2 scroll-mt-24 border-b border-[color:var(--color-rule)] pb-4"
            >
              <CompanyRegionSettings />
            </div>
          ) : null
        }
        onTimezone={(tz) => {
          previewDisplayPref('timezone', tz)
          setAnnouncement(
            `Your timezone: ${tz === 'company' ? 'same as the company' : tz}. Not saved yet.`,
          )
        }}
      />

      <section className="mt-6 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 text-sm text-[color:var(--color-ink-muted)]">
          Put your own settings back to TaskMagix’s standard ones. You’ll see them straight away
          and can still discard them before saving.
          {canManageCompany ? ' Company country and timezone aren’t affected.' : ''}
        </p>
        <Button type="button" variant="secondary" size="sm" onClick={onReset}>
          <RotateCcw size={14} aria-hidden="true" />
          Reset to defaults
        </Button>
      </section>

    </>
  )

  return padded ? <div className="p-8">{body}</div> : <div>{body}</div>
}

/**
 * Region & formats. Every option shows a live sample of itself, drawn in the
 * zone currently previewed. "Saved as" shows the UTC instant underneath — the
 * point of the section: these settings change how a moment is WRITTEN, never
 * the moment that is stored.
 */
function RegionSection({
  effective,
  saved,
  row,
  companyTimeZone,
  companyBlock,
  showTimezone,
  onTimezone,
}) {
  // lib/format.js already has the previewed zone applied (display-prefs
  // updates it before React re-renders), so samples follow the preview.
  const zone = getDisplayTimeZone()
  const now = DateTime.now().setZone(zone || 'local')
  const sample = (text, label) => (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="font-semibold tabular-nums">{text}</span>
      {label ? <span className="text-xs opacity-80">{label}</span> : null}
    </span>
  )
  return (
    <section
      aria-labelledby="prefs-region"
      className="mt-6 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm"
    >
      <SectionHeader id="prefs-region" icon={Globe2} title="Region & formats" />
      <div className="px-6 grid grid-cols-1 xl:grid-cols-2 xl:gap-x-10">
        {companyBlock}
        {showTimezone ? (
          <TimezoneRow
            value={effective.timezone}
            savedValue={saved.timezone}
            companyTimeZone={companyTimeZone}
            onConfirm={onTimezone}
          />
        ) : null}
        {row('dateFormat', {
          description: 'Which part of a date comes first. 03/04/2026 is two different days.',
          renderOption: (o) => sample(now.toFormat(DATE_SAMPLE[o.value]), o.label),
        })}
        {row('timeFormat', {
          description: 'A 24-hour clock, or am and pm.',
          renderOption: (o) => sample(upper(now.toFormat(TIME_SAMPLE[o.value])), o.label),
        })}
        {row('weekStart', {
          description:
            'Where your calendars and date pickers start, and your own weekly views. Shared team reports follow your company’s working week (Reminders & Schedule).',
        })}
        {row('numberFormat', {
          description:
            'How thousands and decimals are written. A currency symbol always comes from the amount itself.',
          renderOption: (o) => sample(o.label),
        })}
      </div>
      <p className="mx-6 mb-5 mt-1 text-xs text-[color:var(--color-ink-muted)]">
        <strong className="font-semibold">Every time is saved in UTC.</strong>{' '}
        {showTimezone
          ? 'Your timezone and formats only decide how it is written for you — on screen, in exports, in your emails and for your own reminders.'
          : `Your formats only decide how it is written for you — on screen, in exports and in your emails. Times show in ${companyTimeZone ? 'the company timezone' : 'this browser’s timezone'}.`}{' '}
        Right now:{' '}
        <span className="font-semibold text-[color:var(--color-ink)] tabular-nums">
          {formatDateTime(now.toJSDate())} {now.toFormat('ZZZZ')}
        </span>{' '}
        · saved as{' '}
        <code className="text-[color:var(--color-ink)]">
          {now.toUTC().toISO({ suppressMilliseconds: true })}
        </code>
      </p>
    </section>
  )
}

/**
 * "Your timezone": the same as the company (the default — no second picker on
 * the page), or a different one, which reveals the zone picker. Choosing a
 * zone — or going back to the company's — opens a confirmation showing the
 * same moment "now" and "after", both saved as the identical UTC instant. The
 * radios and <select> are controlled by the actual setting, so dismissing the
 * dialog by any route (Cancel, Escape, backdrop) leaves them showing what is
 * really set.
 */
function TimezoneRow({ value, savedValue, companyTimeZone, onConfirm }) {
  const [pending, setPending] = useState(null)
  // "A different timezone" picked but no zone chosen yet. Remembered against
  // the value it was picked for, so a Discard / Reset / save that changes the
  // setting drops it without an effect.
  const [choosingFor, setChoosingFor] = useState(null)
  const own = value !== 'company'
  const showPicker = own || choosingFor === value
  // The formatter's validated company zone; the session value is the fallback
  // for the moment between a company-timezone save and the formatter update.
  const companyZone = getCompanyTimeZone() || companyTimeZone
  const zones = useMemo(() => {
    const list =
      typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []
    return value !== 'company' && !list.includes(value) ? [value, ...list] : list
  }, [value])
  const nowInstant = DateTime.now()
  const zoneOf = (v) => (v === 'company' ? companyZone : v) || undefined
  const clock = (z) => upper(nowInstant.setZone(z || 'local').toFormat(timePattern()))
  const pretty = (z) => z.replace(/_/g, ' ')
  const companyLabel = companyZone
    ? `${pretty(companyZone)} — ${clock(companyZone)}`
    : 'this browser’s timezone'
  const writtenIn = (z) => {
    const dt = nowInstant.setZone(z || 'local')
    return `${upper(dt.toFormat(`${datePattern()} ${timePattern()}`))} ${dt.toFormat('ZZZZ')}`
  }
  const savedAs = nowInstant.toUTC().toISO({ suppressMilliseconds: true })
  const changed = value !== savedValue
  const pendingZone = pending ? zoneOf(pending) : undefined
  return (
    <fieldset
      aria-describedby="pref-timezone-desc"
      className="m-0 min-w-0 border-0 border-b border-[color:var(--color-rule)] px-0 py-4 xl:col-span-2"
    >
      <legend className="float-left w-full p-0 flex flex-wrap items-center gap-2 text-sm font-medium text-[color:var(--color-ink-strong)]">
        Your timezone
        {changed ? (
          <span
            title="Changed — not saved yet"
            className="rounded-full border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] px-2 py-0.5 text-xs font-semibold text-[color:var(--color-accent-dim)]"
          >
            Not saved
          </span>
        ) : null}
      </legend>
      <p id="pref-timezone-desc" className="clear-left mt-1 mb-0 text-sm text-[color:var(--color-ink-muted)]">
        Every time on your screens, in your exports and emails, and your own reminders, is written in
        this zone. It never changes anything that is saved.
      </p>
      <div className="mt-3 flex flex-col gap-2">
        <label className="flex items-center gap-2 text-sm text-[color:var(--color-ink)] cursor-pointer">
          <input
            type="radio"
            name="pref-timezone-mode"
            checked={!showPicker}
            onChange={() => {
              setChoosingFor(null)
              if (own) setPending('company')
            }}
            className="h-4 w-4 accent-[color:var(--color-primary)] cursor-pointer"
          />
          <span>
            Same as the company{' '}
            <span className="text-[color:var(--color-ink-muted)] tabular-nums">({companyLabel})</span>
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm text-[color:var(--color-ink)] cursor-pointer">
          <input
            type="radio"
            name="pref-timezone-mode"
            checked={showPicker}
            onChange={() => setChoosingFor(value)}
            className="h-4 w-4 accent-[color:var(--color-primary)] cursor-pointer"
          />
          A different timezone, just for me
        </label>
      </div>
      {showPicker ? (
        <select
          id="pref-timezone"
          aria-label="Your timezone"
          value={own ? value : ''}
          onChange={(e) => {
            if (e.target.value && e.target.value !== value) setPending(e.target.value)
          }}
          className="mt-3 ml-6 w-[calc(100%-1.5rem)] max-w-xl h-10 rounded-lg border border-[color:var(--color-rule-strong)] bg-[color:var(--color-card)] px-3 text-sm text-[color:var(--color-ink)]"
        >
          {own ? null : (
            <option value="" disabled>
              Choose a timezone…
            </option>
          )}
          {zones.map((z) => (
            <option key={z} value={z}>
              {pretty(z)} — {clock(z)}
            </option>
          ))}
        </select>
      ) : null}

      <Modal
        open={pending != null}
        onClose={() => setPending(null)}
        dismissOnBackdrop
        title="Changing your timezone"
        description={`Moving to ${pending === 'company' ? 'your company’s timezone' : pretty(pending ?? '')} changes how times are written for you. It does not change anything that is saved.`}
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => {
                onConfirm(pending)
                setPending(null)
              }}
            >
              Use this timezone
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-[color:var(--color-rule)] p-3">
            <p className="m-0 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-muted)]">
              Now shows as
            </p>
            <p className="m-0 mt-1 text-sm font-semibold text-[color:var(--color-ink-strong)] tabular-nums">
              {writtenIn(zoneOf(value))}
            </p>
            <p className="m-0 mt-1 text-xs text-[color:var(--color-ink-muted)]">saved as {savedAs}</p>
          </div>
          <div className="rounded-lg border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] p-3">
            <p className="m-0 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-accent-dim)]">
              Will show as
            </p>
            <p className="m-0 mt-1 text-sm font-semibold text-[color:var(--color-ink-strong)] tabular-nums">
              {pending ? writtenIn(pendingZone) : ''}
            </p>
            <p className="m-0 mt-1 text-xs text-[color:var(--color-ink-muted)]">saved as {savedAs}</p>
          </div>
        </div>
        <ul className="mt-4 mb-0 space-y-1.5 pl-5 text-sm text-[color:var(--color-ink)] list-disc">
          <li>Every date and time on your screens is rewritten in this zone.</li>
          <li>Your exports, your emails and your own reminders use it too.</li>
          <li>
            <strong>Nothing saved changes.</strong> The same moment is still the same moment — it is
            only written differently.
          </li>
        </ul>
      </Modal>
    </fieldset>
  )
}

function SectionHeader({ id, icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 px-6 py-4 border-b border-[color:var(--color-rule)]">
      <Icon size={18} aria-hidden="true" className="text-[color:var(--color-accent-dim)]" />
      <h2
        id={id}
        className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0"
      >
        {title}
      </h2>
    </div>
  )
}

/**
 * One preference as a native radio group: a <fieldset> + <legend>, each option
 * a real <input type="radio"> (so arrow keys, Tab and screen readers work
 * without extra code) drawn as a segmented control. A row whose previewed
 * value differs from the saved one shows a "Not saved" tag — text, not colour.
 */
function OptionGroup({ name, legend, description, hint, options, value, savedValue, onChange, renderOption }) {
  const changed = value !== savedValue
  const descId = `pref-${name}-desc`
  return (
    <fieldset
      aria-describedby={descId}
      className="m-0 min-w-0 border-0 border-b border-[color:var(--color-rule)] last:border-b-0 px-0 py-4"
    >
      <legend className="float-left w-full p-0 text-sm font-medium text-[color:var(--color-ink-strong)]">
        <span className="inline-flex flex-wrap items-center gap-2">
          {legend}
          {changed ? (
            <span
              title="Changed — not saved yet"
              className="rounded-full border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] px-2 py-0.5 text-xs font-semibold text-[color:var(--color-accent-dim)]"
            >
              Not saved
            </span>
          ) : null}
        </span>
      </legend>
      <div className="clear-both" />
      <p id={descId} className="mt-1 mb-0 text-sm text-[color:var(--color-ink-muted)]">
        {description}
        {hint ? <span className="block mt-0.5 text-xs text-[color:var(--color-ink-subtle)]">{hint}</span> : null}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {options.map((o) => {
          const id = `pref-${name}-${o.value}`
          return (
            // `relative` contains the visually-hidden (absolutely positioned)
            // radio. Without it, in a layout whose scroll area isn't a
            // positioned box (the member and Master Admin panels), the hidden
            // radios position against the page and stretch it, so the whole
            // window scrolls past the layout.
            <label key={String(o.value)} htmlFor={id} className="relative cursor-pointer">
              <input
                id={id}
                type="radio"
                name={`pref-${name}`}
                value={String(o.value)}
                checked={value === o.value}
                onChange={() => onChange(o.value)}
                className="peer sr-only"
              />
              <span
                className={
                  'inline-flex min-h-9 items-center rounded-lg border px-3 py-1.5 text-sm transition-colors ' +
                  'border-[color:var(--color-rule-strong)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] ' +
                  'peer-checked:border-[color:var(--color-accent)] peer-checked:bg-[color:var(--color-accent-bg)] ' +
                  'peer-checked:text-[color:var(--color-accent-strong)] peer-checked:font-semibold ' +
                  'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[color:var(--color-accent)]'
                }
              >
                {renderOption ? renderOption(o) : o.label}
              </span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}
